/**
 * Single product page: gallery (thumbnail crossfade + fullscreen lightbox)
 * and the sticky mobile add-to-cart bar.
 */
export function initSingleProduct() {
	document.querySelectorAll( '.pdp__gallery' ).forEach( initGallery );
	initStickyBar();
}

function initGallery( gallery ) {
	const mainImage  = gallery.querySelector( '.pdp__main-image' );
	const thumbnails = [ ...gallery.querySelectorAll( '.pdp__thumbnail' ) ];
	const gsap       = window.gsap;

	if ( ! mainImage ) return;

	const images = thumbnails.length
		? thumbnails.map( ( t ) => ( {
			image:  t.dataset.image,
			srcset: t.dataset.srcset || '',
			full:   t.dataset.full || t.dataset.image,
			alt:    t.dataset.alt || '',
		} ) )
		: ( mainImage.dataset.full
			? [ { image: mainImage.currentSrc || mainImage.src, srcset: '', full: mainImage.dataset.full, alt: mainImage.alt } ]
			: [] );

	let activeIndex = 0;

	function showImage( index, { animate = true } = {} ) {
		const entry = images[ index ];
		if ( ! entry ) return;
		activeIndex = index;

		const applySrc = () => {
			mainImage.src = entry.image;
			mainImage.alt = entry.alt;
			if ( entry.srcset ) {
				mainImage.srcset = entry.srcset;
			} else {
				mainImage.removeAttribute( 'srcset' );
			}
		};

		if ( gsap && animate && ! window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches ) {
			gsap.to( mainImage, {
				opacity: 0,
				duration: 0.16,
				ease: 'power1.in',
				onComplete() {
					applySrc();
					gsap.fromTo( mainImage, { opacity: 0 }, { opacity: 1, duration: 0.22, ease: 'power1.out' } );
				},
			} );
		} else {
			applySrc();
		}

		thumbnails.forEach( ( item, i ) => {
			const isActive = i === index;
			item.classList.toggle( 'is-active', isActive );
			item.setAttribute( 'aria-pressed', String( isActive ) );
		} );
	}

	thumbnails.forEach( ( thumbnail, index ) => {
		thumbnail.addEventListener( 'click', () => showImage( index ) );
	} );

	initLightbox( gallery, images, () => activeIndex, showImage );
}

// ── Fullscreen lightbox ──────────────────────────────────────────────────────

function initLightbox( gallery, images, getActiveIndex, showImage ) {
	const lightbox = document.querySelector( '[data-pdp-lightbox]' );
	const openBtn  = gallery.querySelector( '[data-pdp-lightbox-open]' );
	if ( ! lightbox || ! openBtn || ! images.length ) return;

	const img       = lightbox.querySelector( '[data-pdp-lightbox-image]' );
	const closeEls  = lightbox.querySelectorAll( '[data-pdp-lightbox-close]' );
	const prevBtn   = lightbox.querySelector( '[data-pdp-lightbox-prev]' );
	const nextBtn   = lightbox.querySelector( '[data-pdp-lightbox-next]' );
	let lastFocused = null;

	function render() {
		const entry = images[ getActiveIndex() ];
		if ( ! entry || ! img ) return;
		img.src = entry.full;
		img.alt = entry.alt;
	}

	function open() {
		lastFocused = document.activeElement;
		render();
		lightbox.classList.add( 'is-open' );
		lightbox.classList.toggle( 'has-multiple', images.length > 1 );
		lightbox.removeAttribute( 'aria-hidden' );
		document.body.classList.add( 'menu-open' );
		closeEls[ 0 ]?.focus?.();
	}

	function close() {
		lightbox.classList.remove( 'is-open' );
		lightbox.setAttribute( 'aria-hidden', 'true' );
		document.body.classList.remove( 'menu-open' );
		lastFocused?.focus?.();
	}

	function step( delta ) {
		const next = ( getActiveIndex() + delta + images.length ) % images.length;
		showImage( next, { animate: false } );
		render();
	}

	openBtn.addEventListener( 'click', open );
	closeEls.forEach( ( el ) => el.addEventListener( 'click', close ) );
	prevBtn?.addEventListener( 'click', () => step( -1 ) );
	nextBtn?.addEventListener( 'click', () => step( 1 ) );

	document.addEventListener( 'keydown', ( e ) => {
		if ( ! lightbox.classList.contains( 'is-open' ) ) return;
		if ( e.key === 'Escape' ) close();
		if ( e.key === 'ArrowLeft' ) step( -1 );
		if ( e.key === 'ArrowRight' ) step( 1 );
	} );

	// Basic touch swipe (left/right) to move between images.
	let touchStartX = null;
	lightbox.addEventListener( 'touchstart', ( e ) => {
		touchStartX = e.touches[ 0 ]?.clientX ?? null;
	}, { passive: true } );
	lightbox.addEventListener( 'touchend', ( e ) => {
		if ( touchStartX === null ) return;
		const deltaX = ( e.changedTouches[ 0 ]?.clientX ?? touchStartX ) - touchStartX;
		if ( Math.abs( deltaX ) > 40 ) step( deltaX > 0 ? -1 : 1 );
		touchStartX = null;
	}, { passive: true } );
}

// ── Sticky mobile add-to-cart bar ────────────────────────────────────────────

function initStickyBar() {
	const bar = document.querySelector( '[data-pdp-sticky-bar]' );
	const anchorBtn = document.querySelector( '.pdp .single_add_to_cart_button' );
	const stickyCta = bar?.querySelector( '[data-pdp-sticky-cta]' );
	if ( ! bar || ! anchorBtn || typeof IntersectionObserver === 'undefined' ) return;

	const observer = new IntersectionObserver(
		( entries ) => {
			entries.forEach( ( entry ) => {
				bar.classList.toggle( 'is-visible', ! entry.isIntersecting );
			} );
		},
		{ rootMargin: '0px 0px -10% 0px' }
	);
	observer.observe( anchorBtn );

	stickyCta?.addEventListener( 'click', () => {
		anchorBtn.scrollIntoView( { behavior: 'smooth', block: 'center' } );
		window.setTimeout( () => anchorBtn.focus(), 350 );
	} );
}
